import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Download, Upload, X, Loader2, AlertTriangle } from "lucide-react";

const ACTION = {
  create: ["Nuevo", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  update: ["Actualizar", "bg-blue-50 text-blue-700 border-blue-200"],
  duplicate: ["Duplicado (se omite)", "bg-amber-50 text-amber-700 border-amber-200"],
  error: ["Error (se omite)", "bg-red-50 text-red-700 border-red-200"],
};
const fmtVal = (v) => (v === null || v === undefined || v === "" ? "—" : typeof v === "boolean" ? (v ? "Sí" : "No") : String(v));

const Check = ({ checked, onChange, label, testid }) => (
  <label className="flex items-center gap-2 text-sm">
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-blue-600" data-testid={testid} />
    {label}
  </label>
);

const PreviewRow = ({ r }) => (
  <li className="py-2 text-sm" data-testid={`import-row-${r.row}`}>
    <div className="flex items-center gap-2 flex-wrap">
      <span className="font-mono text-[11px] text-slate-400">Fila {r.row}</span>
      <span className={`text-[10px] font-semibold border rounded-full px-2 py-0.5 ${ACTION[r.action][1]}`}>{ACTION[r.action][0]}</span>
      <span className="font-medium truncate">{r.name || "(sin nombre)"}</span>
    </div>
    {r.changes.map((c) => (
      <div key={c.field} className={`text-xs ml-1 ${c.field === "cost" || ["price", "price_min", "price_max", "price_from", "price_type"].includes(c.field) ? "text-amber-700" : "text-slate-600"}`}>
        {c.label}: <span className="line-through text-slate-400">{fmtVal(c.from)}</span> → <b>{fmtVal(c.to)}</b>
      </div>
    ))}
    {r.errors.map((e) => <div key={e} className="text-xs text-red-600 ml-1">{e}</div>)}
    {r.duplicate_of && (
      <div className="text-xs text-amber-700 ml-1">
        Ya existe {r.duplicate_of.id ? `(id ${r.duplicate_of.id.slice(0, 8)}${r.duplicate_of.active === false ? ", inactivo" : ""})` : `en la fila ${r.duplicate_of.row}`}
      </div>
    )}
  </li>
);

export const ServicesImportExport = ({ onApplied }) => {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");
  const [opts, setOpts] = useState({ prices: false, costs: false, create: true });

  async function exportXlsx() {
    setBusy("export");
    try {
      const r = await api.get("/admin/services/export", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `catalogo_servicios_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("No se pudo exportar el catálogo"); }
    finally { setBusy(""); }
  }

  async function pick(e) {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy("preview");
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/admin/services/import/preview", fd);
      setFile(f);
      setPreview(r.data);
      setOpts({ prices: false, costs: false, create: true });
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(""); }
  }

  async function apply() {
    setBusy("apply");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("apply_prices", opts.prices);
      fd.append("apply_costs", opts.costs);
      fd.append("create_new", opts.create);
      const r = await api.post("/admin/services/import/apply", fd);
      toast.success(`Importación aplicada · ${r.data.created} nuevos · ${r.data.updated} actualizados`);
      setPreview(null);
      onApplied?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(""); }
  }

  const s = preview?.summary;
  const visible = (preview?.rows || []).filter((r) => r.action !== "unchanged");
  return (
    <>
      <button onClick={exportXlsx} disabled={!!busy} className="btn-secondary" data-testid="services-export-btn">
        {busy === "export" ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />} Exportar Excel
      </button>
      <button onClick={() => fileRef.current?.click()} disabled={!!busy} className="btn-secondary" data-testid="services-import-btn">
        {busy === "preview" ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Importar Excel/CSV
      </button>
      <input ref={fileRef} type="file" accept=".xlsx,.csv" className="hidden" onChange={pick} data-testid="services-import-file" />
      {preview && createPortal(
        <div className="fixed inset-0 z-[60] bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-3" onClick={() => setPreview(null)}>
          <div className="bg-white w-full sm:max-w-2xl rounded-t-2xl sm:rounded-2xl shadow-2xl border border-slate-200 max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="import-preview-dialog">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
              <h3 className="font-heading font-bold">Vista previa de importación · {file?.name}</h3>
              <button onClick={() => setPreview(null)} className="p-1 rounded hover:bg-slate-100"><X size={18} /></button>
            </div>
            <div className="px-4 py-3 grid grid-cols-3 sm:grid-cols-6 gap-2 text-center border-b border-slate-100" data-testid="import-summary">
              {[["Filas", s.total], ["Nuevos", s.create], ["Actualizar", s.update], ["Sin cambios", s.unchanged], ["Duplicados", s.duplicate], ["Errores", s.error]].map(([k, v]) => (
                <div key={k} className="rounded-lg bg-slate-50 border border-slate-200 py-1.5">
                  <div className="text-[10px] text-slate-500">{k}</div>
                  <div className="font-heading font-extrabold">{v}</div>
                </div>
              ))}
            </div>
            <ul className="flex-1 overflow-y-auto px-4 divide-y divide-slate-100">
              {visible.length === 0 && <li className="py-8 text-center text-slate-500 text-sm">El archivo no tiene cambios respecto del catálogo actual.</li>}
              {visible.map((r) => <PreviewRow key={r.row} r={r} />)}
            </ul>
            <div className="border-t border-slate-200 p-4 space-y-2 pb-[max(1rem,env(safe-area-inset-bottom))]">
              {(s.price_changes > 0 || s.cost_changes > 0) && (
                <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2">
                  <AlertTriangle size={14} className="shrink-0 mt-0.5" /> Los precios y costos solo se sobrescriben si marcás la confirmación correspondiente.
                </div>
              )}
              <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-4">
                <Check checked={opts.create} onChange={(v) => setOpts({ ...opts, create: v })} label={`Crear servicios nuevos (${s.create})`} testid="import-opt-create" />
                <Check checked={opts.prices} onChange={(v) => setOpts({ ...opts, prices: v })} label={`Confirmo cambios de precio (${s.price_changes})`} testid="import-opt-prices" />
                <Check checked={opts.costs} onChange={(v) => setOpts({ ...opts, costs: v })} label={`Confirmo cambios de costo (${s.cost_changes})`} testid="import-opt-costs" />
              </div>
              <div className="flex gap-2 justify-end">
                <button onClick={() => setPreview(null)} className="btn-secondary">Cancelar</button>
                <button onClick={apply} disabled={busy === "apply" || (s.create === 0 && s.update === 0)} className="btn-primary disabled:opacity-50" data-testid="import-apply-btn">
                  {busy === "apply" ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Aplicar importación
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};
